import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useServiceLogs } from '@/hooks/useServiceLogs';
import { useClients } from '@/hooks/useClients';
import { useAuth } from '@/contexts/AuthContext';
import { logActivity } from '@/hooks/useActivityLog';
import { Activity, Coffee, Shirt, Utensils, Droplet, Plus, Printer, ChevronLeft, ChevronRight, ChevronDown, Trash2 } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays, addWeeks, addMonths, subDays, subWeeks, subMonths, format, isWithinInterval } from 'date-fns';
import { BarChart, Bar, Cell, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { ReportButton } from '@/components/reports/ReportButton';
import { printServiceReport } from './ServicePrintReport';
import { MealsPanel } from '@/components/services/MealsPanel';
import { MealAnalyticsSection } from './MealAnalyticsSection';

const normalizeServiceType = (type: string): string => {
  const lower = type.toLowerCase();
  if (lower === 'breakfast') return 'breakfast';
  if (lower === 'lunch') return 'lunch';
  if (lower === 'dinner') return 'dinner';
  if (lower === 'milk') return 'milk';
  if (lower === 'juice') return 'juice';
  if (lower.includes('metro') || lower.includes('transit')) return 'metro';
  if (lower.includes('laundry') || lower.includes('wash')) return 'laundry';
  if (lower.includes('meal') || lower.includes('food')) return 'meal';
  if (lower.includes('beverage') || lower.includes('drink')) return 'juice';
  return lower;
};

const serviceIcons: Record<string, any> = {
  metro: Activity, laundry: Shirt, meal: Utensils, breakfast: Coffee,
  lunch: Utensils, dinner: Utensils, milk: Droplet, juice: Droplet,
  hygiene: Coffee, other: Activity,
};

export function EnhancedServicesSection() {
  const { user } = useAuth();
  const { serviceLogs, isLoading, createServiceLog, deleteServiceLog } = useServiceLogs();
  const [pendingDelete, setPendingDelete] = useState<{ id: string; clientId?: string } | null>(null);
  const { clients } = useClients();

  const [view, setView] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [referenceDate, setReferenceDate] = useState(new Date());
  const [isAddServiceOpen, setIsAddServiceOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [serviceType, setServiceType] = useState<string>('metro');
  const [quantity, setQuantity] = useState('1');
  const [notes, setNotes] = useState('');
  const [filterType, setFilterType] = useState<string>('all');

  // Compute date range based on view + referenceDate
  const getDateRange = () => {
    switch (view) {
      case 'daily': return { start: startOfDay(referenceDate), end: endOfDay(referenceDate) };
      case 'weekly': return { start: startOfWeek(referenceDate, { weekStartsOn: 1 }), end: endOfWeek(referenceDate, { weekStartsOn: 1 }) };
      case 'monthly': return { start: startOfMonth(referenceDate), end: endOfMonth(referenceDate) };
    }
  };
  const { start: rangeStart, end: rangeEnd } = getDateRange();

  const navigatePrev = () => {
    if (view === 'daily') setReferenceDate(d => subDays(d, 1));
    else if (view === 'weekly') setReferenceDate(d => subWeeks(d, 1));
    else setReferenceDate(d => subMonths(d, 1));
  };
  const navigateNext = () => {
    if (view === 'daily') setReferenceDate(d => addDays(d, 1));
    else if (view === 'weekly') setReferenceDate(d => addWeeks(d, 1));
    else setReferenceDate(d => addMonths(d, 1));
  };
  const navigateToday = () => setReferenceDate(new Date());

  const dateLabel = view === 'daily'
    ? format(rangeStart, 'EEEE, MMMM d, yyyy')
    : view === 'weekly'
      ? `${format(rangeStart, 'MMM d')} – ${format(rangeEnd, 'MMM d, yyyy')}`
      : format(rangeStart, 'MMMM yyyy');

  // Filter logs by date range and type
  const filteredLogs = serviceLogs.filter(log => {
    if (!log.created_at) return false;
    const logDate = new Date(log.created_at);
    if (!isWithinInterval(logDate, { start: rangeStart, end: rangeEnd })) return false;
    if (filterType !== 'all' && normalizeServiceType(log.service_type) !== filterType) return false;
    return true;
  });

  const handleAddService = async () => {
    if (!selectedClient) { toast.error('Please select a client'); return; }
    const client: any = clients.find((c: any) => c.id === selectedClient);
    createServiceLog({
      clientId: selectedClient,
      serviceType: serviceType as any,
      quantity: parseInt(quantity) || 1,
      notes: notes || undefined,
      siteId: client?.site_id ?? null,
    });
    await logActivity({
      actionType: 'add_service',
      actionDetails: { client_id: selectedClient, service_type: serviceType, quantity: parseInt(quantity) || 1 },
      siteId: client?.site_id ?? undefined,
    });
    setIsAddServiceOpen(false);
    setSelectedClient(''); setServiceType('metro'); setQuantity('1'); setNotes('');
  };

  // Other (non-meal/beverage) service types
  const OTHER_TYPES = [
    { key: 'metro', label: 'Transit', icon: Activity, color: 'text-blue-500' },
    { key: 'laundry', label: 'Laundry', icon: Shirt, color: 'text-purple-500' },
  ];
  const otherCounts = OTHER_TYPES.map(t => ({
    ...t,
    count: filteredLogs.filter(l => normalizeServiceType(l.service_type) === t.key).reduce((s, l) => s + (l.quantity || 1), 0),
  }));

  // Distribution chart for ALL service types
  const ALL_TYPES = [
    { key: 'breakfast', label: 'Breakfast', color: '#22c55e' },
    { key: 'lunch', label: 'Lunch', color: '#eab308' },
    { key: 'dinner', label: 'Dinner', color: '#f97316' },
    { key: 'milk', label: 'Milk', color: '#06b6d4' },
    { key: 'juice', label: 'Juice', color: '#ec4899' },
    { key: 'metro', label: 'Transit', color: '#3b82f6' },
    { key: 'laundry', label: 'Laundry', color: '#a855f7' },
  ];
  const barData = ALL_TYPES.map(t => ({
    name: t.label,
    count: filteredLogs.filter(l => normalizeServiceType(l.service_type) === t.key).reduce((s, l) => s + (l.quantity || 1), 0),
    fill: t.color,
  }));

  const [logsOpen, setLogsOpen] = useState(false);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">Service Tracking</h2>
          <p className="text-sm text-muted-foreground">Meals, beverages, transit, laundry — received, distributed, discarded</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" onClick={navigatePrev}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" onClick={navigateToday}>Today</Button>
            <Button variant="outline" size="icon" onClick={navigateNext}><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <Button variant="outline" size="sm" onClick={() => printServiceReport(filteredLogs as any, clients as any, view, rangeStart, rangeEnd)}>
            <Printer className="h-4 w-4 mr-2" /> Print
          </Button>
          <ReportButton
            title="Service Logs"
            label="Export"
            rows={filteredLogs as any[]}
            columns={[
              { key: 'date', label: 'Date', value: (l: any) => (l.created_at ? format(new Date(l.created_at), 'yyyy-MM-dd HH:mm') : '—') },
              { key: 'client', label: 'Client', value: (l: any) => (clients as any[]).find((c: any) => c.id === l.client_id)?.name || '—' },
              { key: 'service', label: 'Service', value: (l: any) => l.service_type || '—' },
              { key: 'qty', label: 'Quantity', value: (l: any) => l.quantity ?? 1 },
              { key: 'notes', label: 'Notes', value: (l: any) => l.notes || '', defaultOn: false },
            ]}
            getDate={(l: any) => l.created_at}
            getSiteId={(l: any) => l.site_id}
            summary={(rows) => ({
              'Entries': rows.length,
              'Total Quantity': rows.reduce((s: number, l: any) => s + (l.quantity || 1), 0),
            })}
          />
          <Dialog open={isAddServiceOpen} onOpenChange={setIsAddServiceOpen}>
            <DialogTrigger asChild>
              <Button size="sm"><Plus className="h-4 w-4 mr-2" /> Log Service</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Log Service</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Client</Label>
                  <Select value={selectedClient} onValueChange={setSelectedClient}>
                    <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>
                      {clients.map((client: any) => (
                        <SelectItem key={client.id} value={client.id}>{client.first_name} {client.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Service Type</Label>
                  <Select value={serviceType} onValueChange={(v: any) => setServiceType(v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="metro">Transit</SelectItem>
                      <SelectItem value="laundry">Laundry</SelectItem>
                      <SelectItem value="breakfast">Breakfast</SelectItem>
                      <SelectItem value="lunch">Lunch</SelectItem>
                      <SelectItem value="dinner">Dinner</SelectItem>
                      <SelectItem value="milk">Milk</SelectItem>
                      <SelectItem value="juice">Juice</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Quantity</Label>
                  <Input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
                </div>
                <div>
                  <Label>Notes (Optional)</Label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any additional notes..." />
                </div>
                <Button onClick={handleAddService} className="w-full">Log Service</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={view} onValueChange={(v: any) => { setView(v); setReferenceDate(new Date()); }}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <TabsList>
            <TabsTrigger value="daily">Daily</TabsTrigger>
            <TabsTrigger value="weekly">Weekly</TabsTrigger>
            <TabsTrigger value="monthly">Monthly</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">{dateLabel}</span>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[140px] h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="metro">Transit</SelectItem>
                <SelectItem value="laundry">Laundry</SelectItem>
                <SelectItem value="breakfast">Breakfast</SelectItem>
                <SelectItem value="lunch">Lunch</SelectItem>
                <SelectItem value="dinner">Dinner</SelectItem>
                <SelectItem value="milk">Milk</SelectItem>
                <SelectItem value="juice">Juice</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <TabsContent value={view} className="space-y-4 mt-4">
          {/* Today's intake (daily only) */}
          {view === 'daily' && <MealsPanel date={referenceDate} />}

          {/* Lifecycle summary cards + chart + top items */}
          <MealAnalyticsSection rangeStart={rangeStart} rangeEnd={rangeEnd} />

          {/* Other services (Transit / Laundry) + overall distribution chart side-by-side */}
          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-1">
              <CardHeader className="py-3"><CardTitle className="text-base">Other Services</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {otherCounts.map(t => {
                  const Icon = t.icon;
                  return (
                    <div key={t.key} className="flex items-center justify-between p-2 rounded-md border border-border">
                      <div className="flex items-center gap-2">
                        <Icon className={`h-4 w-4 ${t.color}`} />
                        <span className="text-sm font-medium">{t.label}</span>
                      </div>
                      <span className="text-xl font-bold">{t.count}</span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
            <Card className="lg:col-span-2">
              <CardHeader className="py-3"><CardTitle className="text-base">Service Distribution</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {barData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Recent logs — collapsible */}
          <Card>
            <Collapsible open={logsOpen} onOpenChange={setLogsOpen}>
              <CollapsibleTrigger asChild>
                <CardHeader className="py-3 cursor-pointer hover:bg-muted/50 transition-colors flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Recent Service Logs ({filteredLogs.length})</CardTitle>
                  <ChevronDown className={`h-4 w-4 transition-transform ${logsOpen ? 'rotate-180' : ''}`} />
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Client</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead>Qty</TableHead>
                        <TableHead>Date/Time</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredLogs.slice(0, 20).map(log => {
                        const client: any = clients.find(c => c.id === log.client_id);
                        const clientName = client?.name || (client?.first_name ? `${client.first_name} ${client.last_name || ''}`.trim() : 'Unknown Client');
                        const normalized = normalizeServiceType(log.service_type);
                        const Icon = serviceIcons[normalized] || Activity;
                        const displayName = { metro: 'Transit', laundry: 'Laundry', meal: 'Meal', breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner', milk: 'Milk', juice: 'Juice' }[normalized] || log.service_type;
                        return (
                          <TableRow key={log.id} className="group">
                            <TableCell>{clientName}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2"><Icon className="h-4 w-4" /><span>{displayName}</span></div>
                            </TableCell>
                            <TableCell>{log.quantity || 1}</TableCell>
                            <TableCell>{format(new Date(log.created_at!), 'MMM d, yyyy h:mm a')}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{log.notes || '-'}</TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                                onClick={() => setPendingDelete({ id: log.id, clientId: log.client_id })}
                                aria-label="Remove service entry"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this service entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This will hide the entry from logs and analytics. The action will be recorded in the activity log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDelete) deleteServiceLog(pendingDelete);
                setPendingDelete(null);
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
